// Keeping this hook for a future ticket when we integrate the autocomplete in various places in both apps
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Suggestion } from "../../../components/dist/components/forms/FormComponents.types";
import {
  AddressComponent,
  PlaceAddressResponse,
  useGetAddressDetailsQuery,
  useGetAddressQuery,
} from "../contexts/endpoints/shared/addresses.api";

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

function byType(components: AddressComponent[], type: string) {
  return components.find((c) => c.types?.includes(type));
}

export function buildUsAddress(input: PlaceAddressResponse): Address {
  const addressComponent = input.place;

  if (!addressComponent) {
    return { line1: "", line2: "", city: "", state: "", zip: "", country: "" };
  }

  const subpremise = byType(addressComponent, "subpremise")?.longText?.trim();
  const streetNumber = byType(
    addressComponent,
    "street_number"
  )?.longText?.trim();
  const routeLong = byType(addressComponent, "route")?.longText?.trim();
  const routeShort = byType(addressComponent, "route")?.shortText?.trim();
  const route = routeLong || routeShort || "";
  const city = byType(addressComponent, "locality")?.longText?.trim() || "";
  const state =
    byType(
      addressComponent,
      "administrative_area_level_1"
    )?.shortText?.trim() || "";
  const zip = byType(addressComponent, "postal_code")?.longText?.trim() || "";
  const country = byType(addressComponent, "country")?.shortText?.trim() || "";

  // Line 1: street only (NO subpremise)
  const line1 = [streetNumber, route]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  // Line 2: subpremise only
  const line2 = subpremise || "";

  return { line1, line2, city, state, zip, country };
}

/**
 * Custom hook to manage address autocomplete logic.
 * @param onSelect - Optional callback when an address is selected.
 * @returns An object with state and handlers for the address autocomplete.
 */
export const useAddressAutocomplete = () => {
  const [searchString, setSearchTerm] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [formattedAddress, setFormattedAddress] = useState<Address | null>(
    null
  );

  // Lazy query to fetch full details for a selected address.
  const {
    data: rawAddressDetails,
    isFetching: isDetailsLoading,
    error: detailsError,
  } = useGetAddressDetailsQuery(
    { placeId },
    {
      skip: !placeId,
    }
  );

  useEffect(() => {
    const address = rawAddressDetails
      ? buildUsAddress(rawAddressDetails)
      : null;
    setFormattedAddress(address);
  }, [rawAddressDetails]);

  // This query will automatically run when `searchTerm` is updated and has enough characters.
  const {
    data,
    isFetching: suggestionsLoading,
    error: suggestionsError,
  } = useGetAddressQuery(
    { searchString },
    { skip: searchString.length <= 3 } // Skip the query if the search term is too short
  );

  // Debounce the state update to avoid excessive API calls while typing.
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 300),
    []
  );

  // Cancel any pending debounced calls when the hook unmounts
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm]
  );

  // Update suggestions when data from the API changes
  useEffect(() => {
    // Only show suggestions if the query is not loading and the search string is valid.
    // This prevents showing stale data when the input is cleared.
    const suggestionsFromData = data?.suggestions as Suggestion[] | undefined;
    if (suggestionsFromData) {
      setSuggestions(suggestionsFromData);
    } else {
      // Clear suggestions if there's no data (e.g., after an empty search)
      setSuggestions([]);
    }
  }, [data, searchString]);

  const onLookupAddress = useCallback(
    (value: string) => {
      if (value && value.length > 2) {
        // Only search if input is > 2 chars
        debouncedSetSearchTerm(value);
      } else {
        debouncedSetSearchTerm.cancel(); // Cancel any pending search
        setSuggestions([]);
      }
    },
    [debouncedSetSearchTerm]
  );

  const onClearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  const onAddressSelected = useCallback((placeId: string) => {
    setSuggestions([]);
    setPlaceId(placeId);
  }, []);

  const details = useMemo(
    () => ({
      data: formattedAddress,
      isLoading: isDetailsLoading,
      error: detailsError,
    }),
    [formattedAddress, isDetailsLoading, detailsError]
  );

  return {
    suggestions: {
      items: suggestions,
      isLoading: suggestionsLoading,
      error: suggestionsError,
    },
    details,
    onLookupAddress,
    onClearSuggestions,
    onAddressSelected,
  };
};
